import { zodResolver } from '@hookform/resolvers/zod';
import { MessageType, useUpdateRateLimitMutation } from 'api/MessageTypeAPI';
import { SubscriptionPolicy } from 'modules/graphql';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { ApiError } from 'services/api';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { BetaBadge } from 'ui/badge';
import { InputText } from 'ui/inputs';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { RadioList } from 'ui/radioList';
import { Body2 } from 'ui/theme';
import * as z from 'zod';
import {
  MESSAGE_TYPE_FIELDS_TEXT,
  RATE_LIMIT_OPTIONS,
  SCHEMA_ERROR_TEXT,
  maxIntValue,
} from '../../constants';
import { useChannelsContext } from '../../contexts/ChannelsContext';
import { HeaderWrapper, InputFieldWrapper } from './UpdateRateLimitModal.styled';

const { RATE_LIMIT, RATE_LIMIT_OPTION, VALUES } = MESSAGE_TYPE_FIELDS_TEXT;
const { CUSTOM, NONE } = VALUES;

export interface ModalProps {
  messageType: MessageType;
  onClose: () => void;
}

type RateLimitValues = {
  rateLimit: string;
  rateLimitOption: string;
};

const schema = z
  .object({
    rateLimit: z.string().regex(/^[0-9]+$/, { message: SCHEMA_ERROR_TEXT.regex }),
    rateLimitOption: z.enum([CUSTOM, NONE]),
  })
  .transform(({ rateLimit, rateLimitOption }) => ({
    rateLimit: parseInt(rateLimit, 10),
    rateLimitOption,
  }))
  .refine((values) => (values.rateLimitOption === NONE ? true : values.rateLimit > 0), {
    message: SCHEMA_ERROR_TEXT.minValue,
    path: ['rateLimit'],
  })
  .refine((values) => (values.rateLimitOption === NONE ? true : values.rateLimit <= maxIntValue), {
    message: SCHEMA_ERROR_TEXT.maxValue,
    path: ['rateLimit'],
  });

export const UPDATE_RATE_LIMIT_TEXT = {
  HEADER: 'Update rate limit?',
  DESCRIPTION:
    'Limit the send rate for new campaigns of this message type. You can override it on a campaign-by-campaign basis.',
  ACTION_BUTTON: 'Save',
  SUCCESS_MESSAGE: 'Success! Updated rate limit:',
  ERROR_MESSAGE: 'Unable to update rate limit.',
};

export const UpdateRateLimitModal = ({ onClose, messageType }: ModalProps) => {
  const { rateLimitPerMin } = messageType;
  const { refetchMessageTypes } = useChannelsContext();
  const dispatch = useDispatch();

  const { mutate } = useUpdateRateLimitMutation({
    onSuccess: () => {
      onClose();
      dispatch(
        addPositiveToast(
          <>
            {UPDATE_RATE_LIMIT_TEXT.SUCCESS_MESSAGE} <strong>{messageType.name}</strong>
          </>,
        ),
      );
      refetchMessageTypes();
    },
    onError: (e: unknown) => {
      const { response } = e as ApiError;
      const errorText = `${UPDATE_RATE_LIMIT_TEXT.ERROR_MESSAGE} ${response?.data}.`;
      onClose();
      dispatch(addNegativeToastWithErrorId(errorText, 'useUpdateRateLimitMutation', e));
    },
  });

  const defaultValues = {
    rateLimit: rateLimitPerMin?.toString() || '',
    rateLimitOption: rateLimitPerMin ? CUSTOM : NONE,
  };

  const {
    control,
    formState: { isSubmitted, isValid },
    handleSubmit,
    watch,
  } = useForm({
    defaultValues,
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    reValidateMode: 'onChange',
  });

  const rateLimitOptionValue = watch('rateLimitOption');
  const isCustom = rateLimitOptionValue === CUSTOM;

  const onSubmit: SubmitHandler<RateLimitValues> = (values) => {
    const isOptionCustom = values.rateLimitOption !== NONE;
    const rateLimit = isOptionCustom ? parseInt(values?.rateLimit, 10) : null;

    if (isOptionCustom && !isValid) return null;

    // Usage of the message type update API needs to send DOI fields at the top level of the object in the update request
    const doiFields =
      messageType.subscriptionPolicy === SubscriptionPolicy.DoubleOptIn
        ? {
            brandName: messageType.subscriptionPolicyMetadata?.brandName,
            messagingInitiative: messageType.subscriptionPolicyMetadata?.messagingInitiative,
            disclaimerLink: messageType.subscriptionPolicyMetadata?.disclaimerLink,
            smsSenderId: messageType.subscriptionPolicyMetadata?.smsSenderId,
          }
        : {};

    const payload = { ...messageType, ...doiFields, rateLimitPerMin: rateLimit };
    mutate(payload);
  };

  return (
    <Modal
      id="update-rate-limit-modal"
      isModalOpen={true}
      onClose={onClose}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>
        <HeaderWrapper>
          {UPDATE_RATE_LIMIT_TEXT.HEADER} <BetaBadge />
        </HeaderWrapper>
      </ModalHeader>
      <ModalBody>
        <Body2>{UPDATE_RATE_LIMIT_TEXT.DESCRIPTION}</Body2>
        <Controller
          control={control}
          name={RATE_LIMIT_OPTION.NAME as keyof RateLimitValues}
          render={({ field: { onChange, value, name } }) => (
            <RadioList
              label="Rate limit"
              name={name}
              onSelect={onChange}
              options={RATE_LIMIT_OPTIONS}
              value={value}
            />
          )}
        />
        {isCustom && (
          <InputFieldWrapper>
            <Controller
              control={control}
              name={RATE_LIMIT.NAME as keyof RateLimitValues}
              render={({ field: { onChange, onBlur, value, name }, fieldState: { error } }) => (
                <FormField errorText={error?.message} hasError={!!error && isCustom}>
                  <InputText
                    id={`${messageType.id}`}
                    isDisabled={rateLimitOptionValue === NONE}
                    maxValue={maxIntValue}
                    minValue={1}
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    Suffix="messages per minute"
                    type="number"
                    value={value}
                  />
                </FormField>
              )}
            />
          </InputFieldWrapper>
        )}
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          onClick={handleSubmit(onSubmit)}
          isDisabled={isSubmitted && !isValid}
          size={BUTTON_SIZES.LARGE}
        >
          {UPDATE_RATE_LIMIT_TEXT.ACTION_BUTTON}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
